import { type AlbumModel } from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { navigateToFx } from '@/shared/lib/providers'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Space, type TableProps, Tag, Typography } from 'antd'
import { useUnit } from 'effector-react'

import { getAlbumTypeColor, getAlbumTypeString } from '../../lib/album-type'
import { type AlbumsSortState } from '../../lib/albums-query-model'
import { COLUMN_WIDTH } from '../../lib/column-width'
import { albumDeleted } from '../../model/album.model'

interface useAlbumsTableColumnsProps {
  onEdit: (albumId: string) => void
  sort: AlbumsSortState
}
export const useAlbumsTableColumns = ({
  onEdit: handleEdit,
  sort,
}: useAlbumsTableColumnsProps): TableProps<AlbumModel>['columns'] => {
  const deleteAlbum = useUnit(albumDeleted)
  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteAlbum({ id })
      },
    })
  }

  return [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      ellipsis: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (name, _) => name,
    },
    {
      title: 'Автор',
      dataIndex: 'author',
      key: 'author',
      width: COLUMN_WIDTH.AUTHOR,
      sorter: true,
      sortOrder:
        sort.field === 'author'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, album) => {
        if (!album.authors) return ''
        const mainAuthors = album.authors.filter((a) => !a.isCoauthor)
        const coAuthors = album.authors.filter((a) => a.isCoauthor)
        const authors = [...mainAuthors, ...coAuthors]
        return (
          <>
            {authors.map((author, i) => {
              const isLast = i === authors.length - 1
              const route = ROUTES.AUTHORS.EDIT.replace(':id', author.id)

              return (
                <span key={author.id}>
                  <Typography.Link
                    onClick={() => void navigateToFx({ to: route })}
                  >
                    {author.nickname}
                  </Typography.Link>
                  {!isLast && ',\u00A0'}
                </span>
              )
            })}
          </>
        )
      },
    },
    {
      title: 'Тип',
      width: COLUMN_WIDTH.TYPE,
      dataIndex: 'type',
      key: 'type',
      render: (_, album) => (
        <Tag color={getAlbumTypeColor(album.type)}>
          {getAlbumTypeString(album.type)}
        </Tag>
      ),
    },
    {
      title: '',
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, album) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(album.id)}
          />
          <Button
            onClick={() => handleDelete(album.id)}
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]
}
